// src/App.js
import React, { useEffect, useState, useContext, lazy, Suspense } from 'react';
import './App.css';
import './DarkMode.css';
import './LightMode.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { AppThemeContext, ThemeProvider } from './context/AppThemeContext';
import { PrereleaseCardContext, ProxyContextProvider } from './context/PrereleaseCardContext';
import { DoubleClickProvider } from './context/DoubleClickContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer'; // Import the new Footer component
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import EnhancedTCGController from './utils/TCGapi/EnhancedTCGController';
// FontAwesomeIcon and faGithub are now used in Footer.js, so not strictly needed here unless used elsewhere in App.js
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; 
// import { faGithub } from '@fortawesome/free-brands-svg-icons';

// Lazy load components for better performance
const EnhancedCardSearchPanel = lazy(() => import('./components/layout/EnhancedCardSearchPanel'));
const DeckViewPanel = lazy(() => import('./components/layout/DeckViewPanel'));

// Create a loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
    <Spinner animation="border" role="status" variant="primary" style={{ width: '3rem', height: '3rem' }}>
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  </div>
);

function App() {
  const { theme } = useContext(AppThemeContext);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Check for mobile device
  useEffect(() => {
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
      return mobile;
    };
    
    if (checkMobile()) {
      // Consider a less intrusive way to inform mobile users, e.g., a small banner
      // alert('PTCG Deck builder is still in mobile development. Please view on a desktop for full functionality.');
    }
  }, []);
  
  // Initialize the Enhanced TCG Controller
  useEffect(() => {
    const initializeApp = async () => {
      try {
        await EnhancedTCGController.initialize();
        
        const loadSavedDecks = async () => {
          try {
            console.log('App initialized successfully');
          } catch (error) {
            console.error('Error loading saved state:', error);
          }
        };
        
        await loadSavedDecks();
        setIsInitialized(true);
      } catch (error) {
        console.error('Initialization error:', error);
        setInitError(error.message || 'Failed to initialize application');
      }
    };
    
    initializeApp();
    
    return () => {
      // Any cleanup code
    };
  }, []);
  
  // Responsive design adjustments
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); 
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Error boundary fallback
  if (initError) {
    return (
      <div className={`App ${theme === 'dark' ? 'dark-theme' : 'light-theme'}`}>
        <Header />
        <div className="container mt-5">
          <Alert variant="danger">
            <Alert.Heading>Initialization Error</Alert.Heading>
            <p>There was a problem initializing the application: {initError}</p>
            <hr />
            <p className="mb-0">
              Please try refreshing the page. If the problem persists, check your internet connection.
            </p>
          </Alert>
        </div>
        {/* Render Footer even on error page for consistency, if desired */}
        <Footer /> 
      </div>
    );
  }
  
  // Main app rendering
  return (
    <div className={`App ${theme === 'dark' ? 'dark-theme' : 'light-theme'}`}>
      <Header />
      <div className="content">
        {!isInitialized ? (
          <LoadingSpinner />
        ) : (
          <ProxyContextProvider>
            <Suspense fallback={<LoadingSpinner />}>
              <div className="enhanced-card-search-panel-container">
                <EnhancedCardSearchPanel />
              </div>
              <div className="deck-view-panel-container">
                <DeckViewPanel />
              </div>
            </Suspense>
          </ProxyContextProvider>
        )}
      </div>
      
      {isMobile && (
        <div className="mobile-warning">
          <Alert variant="warning" className="text-center m-0 py-1"> {/* Reduced padding */}
            <small>Mobile experience may be limited. For best results, use a desktop browser.</small>
          </Alert>
        </div>
      )}

      <Footer /> {/* Use the imported Footer component here */}
    </div>
  );
}

// Error Boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("App error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="container mt-5">
          <Alert variant="danger">
            <Alert.Heading>Something went wrong</Alert.Heading>
            <p>{this.state.error?.message || "An unexpected error occurred"}</p>
            <hr />
            <p className="mb-0">
              Please try refreshing the page. If the problem persists, check the console for more details.
            </p>
          </Alert>
           {/* Optionally render footer on error page too */}
          {/* <Footer /> */}
        </div>
      );
    }

    return this.props.children;
  }
}

// App wrapper with all providers
export default function AppWrapper() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <DoubleClickProvider>
          <App />
        </DoubleClickProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
